/* GRYD v2 — fx: the playful layer. Shared helpers every component calls.
   Rules baked in: never block input (pointer-events: none on every flourish), respect
   prefers-reduced-motion (JS effects no-op or fade), scale by html[data-quirk]. */

const html = () => document.documentElement;
const mq = (q) => { try { return window.matchMedia(q).matches; } catch (e) { return false; } };
const reduced = () => mq('(prefers-reduced-motion: reduce)') || html().getAttribute('data-motion') === 'reduced';
const finePointer = () => mq('(hover: hover) and (pointer: fine)');
const quirk = () => { const q = html().getAttribute('data-quirk'); return q === 'whisper' || q === 'loud' ? q : 'noticeable'; };
const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const once = (key) => { try { const k = 'gryd-once:' + key; if (sessionStorage.getItem(k)) return false; sessionStorage.setItem(k, '1'); return true; } catch (e) { return true; } };
const animate = (el, frames, opts) => { if (!el || !el.animate) return null; try { return el.animate(frames, opts); } catch (e) { return null; } };

/* A fresh tilt every time: stamps never land at the same angle twice. */
function stampAngle() {
  const q = quirk(), span = q === 'loud' ? [3, 9] : q === 'whisper' ? [1, 2.5] : [2, 6];
  return (Math.random() < 0.72 ? -1 : 1) * rand(span[0], span[1]);
}

/* burst — confetti made of GRYD geometry, fired from an element.
   Squares, dots, quarter-circles and triangles in lime/gold/coral/ink, each with an ink edge.
   Fixed layer, pointer-events none, removed when done. Whisper: none. Loud: more. */
const SHAPES = ['square', 'dot', 'quarter', 'tri', 'bar'];
const FILLS = ['var(--lime)', 'var(--gold)', 'var(--coral)', 'var(--lime)', 'var(--gold)', 'var(--inkbg)'];
function burst(el, opts = {}) {
  if (!el || reduced()) return;
  const q = quirk();
  if (q === 'whisper' && !opts.force) return;
  const n = opts.count || (q === 'loud' ? 26 : 16);
  const r = el.getBoundingClientRect();
  const ox = r.left + r.width / 2, oy = r.top + (opts.fromTop ? 0 : r.height / 2);
  const layer = document.createElement('div');
  layer.setAttribute('aria-hidden', 'true');
  layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2147483000;overflow:visible';
  document.body.appendChild(layer);
  let left = n;
  for (let i = 0; i < n; i++) {
    const s = opts.shapes ? pick(opts.shapes) : SHAPES[i % SHAPES.length];
    const size = Math.round(rand(7, s === 'bar' ? 7 : 13));
    const p = document.createElement('i');
    const fill = pick(FILLS);
    let css = `position:absolute;left:${ox - size / 2}px;top:${oy - size / 2}px;width:${s === 'bar' ? size * 2.2 : size}px;height:${size}px;box-sizing:border-box;`;
    if (s === 'tri') css += `width:0;height:0;border-left:${size / 2 + 1}px solid transparent;border-right:${size / 2 + 1}px solid transparent;border-bottom:${size + 2}px solid ${fill};`;
    else css += `background:${fill};border:1.5px solid var(--ink);` + (s === 'dot' ? 'border-radius:50%;' : s === 'quarter' ? 'border-radius:100% 0 0 0;' : s === 'bar' ? 'border-radius:2px;' : 'border-radius:1px;');
    p.style.cssText = css;
    layer.appendChild(p);
    /* a cone upward (the button "fires"), then gravity */
    const ang = rand(-155, -25) * Math.PI / 180;
    const dist = rand(60, q === 'loud' ? 190 : 150);
    const dx = Math.cos(ang) * dist, dy = Math.sin(ang) * dist;
    const fall = rand(70, 150), spin = rand(-540, 540), dur = rand(720, 1100);
    const a = animate(p, [
      { transform: 'translate(0,0) rotate(0) scale(.4)', opacity: 1 },
      { transform: `translate(${dx}px,${dy}px) rotate(${spin * 0.6}deg) scale(1)`, opacity: 1, offset: 0.42 },
      { transform: `translate(${dx * 1.18}px,${dy + fall}px) rotate(${spin}deg) scale(.9)`, opacity: 0 }
    ], { duration: dur, delay: rand(0, 70), easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'both' });
    const done = () => { if (--left <= 0) layer.remove(); };
    if (a) a.onfinish = done; else setTimeout(done, dur + 80);
  }
  setTimeout(() => layer.remove(), 1600);
}

/* shake — the field "shakes its head" (decaying, 6 beats, 360ms). */
function shake(el) {
  if (!el) return;
  if (reduced()) return animate(el, [{ outlineColor: 'var(--coral)' }, { outlineColor: 'transparent' }], { duration: 300 });
  return animate(el, [0, -7, 6, -4, 3, -1, 0].map((x) => ({ transform: `translateX(${x}px)` })), { duration: 360, easing: 'ease-out' });
}

/* boop — a one-shot squash that springs back even if the pointer stays (Comeau's "boop"). */
function boop(el, amt = 1) {
  if (!el || reduced()) return;
  return animate(el, [{ transform: 'scale(1)' }, { transform: `scale(${1 - 0.08 * amt},${1 + 0.05 * amt}) rotate(${-2 * amt}deg)` }, { transform: `scale(${1 + 0.03 * amt}) rotate(${1 * amt}deg)` }, { transform: 'scale(1)' }], { duration: 320, easing: 'cubic-bezier(.34,1.56,.64,1)' });
}

/* wobble — the dizzy spin for five quick taps. */
function wobble(el) {
  if (!el || reduced()) return;
  return animate(el, [0, 18, -16, 12, -8, 360 + 4, 360].map((d) => ({ transform: `rotate(${d}deg)` })), { duration: 900, easing: 'cubic-bezier(.3,.7,.3,1)' });
}

/* tremble — the nervous hover before a big press (Mailchimp's shaking hand). Plays once. */
function tremble(el) {
  if (!el || reduced() || quirk() === 'whisper') return;
  return animate(el, [0, -1.2, 1.2, -1, 1, -.5, 0].map((d) => ({ transform: `rotate(${d}deg)` })), { duration: 280, easing: 'linear' });
}

/* taps — counts quick taps; fires cb on the Nth within windowMs. */
function taps(n, windowMs, cb) {
  let t = []; return () => { const now = Date.now(); t = t.filter((x) => now - x < windowMs); t.push(now); if (t.length >= n) { t = []; cb(); } };
}

/* theme — flip with a View Transition circle from the toggle (click, no hold).
   Wipe is the default; reduced motion or no API: a quick crossfade. */
function theme(next, from) {
  const root = html();
  const apply = () => { root.setAttribute('data-theme', next); try { localStorage.setItem('gryd-theme', next); } catch (e) {} };
  if (reduced() || !document.startViewTransition || !from) {
    root.classList.add('theme-transition'); apply(); setTimeout(() => root.classList.remove('theme-transition'), 300); return;
  }
  const r = from.getBoundingClientRect();
  const x = r.left + r.width / 2, y = r.top + r.height / 2;
  const end = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
  root.classList.add('gx-vt');
  const vt = document.startViewTransition(apply);
  vt.ready.then(() => {
    /* stepped at the start like a print plate landing, smooth after: playful, not pristine */
    root.animate({ clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${Math.min(28, end)}px at ${x}px ${y}px)`, `circle(${end}px at ${x}px ${y}px)`], offset: [0, 0.18, 1] }, { duration: 460, easing: 'cubic-bezier(.3,.8,.25,1)', pseudoElement: '::view-transition-new(root)' });
  }).catch(() => {});
  vt.finished.finally(() => root.classList.remove('gx-vt'));
}

/** The playful layer. Every helper no-ops (or fades) under reduced motion and scales with
    html[data-quirk]. Components reach it as `fx`; prototypes as `GRYD2.fx`. */
export const fx = { reduced, finePointer, quirk, rand, pick, once, animate, stampAngle, burst, shake, boop, wobble, tremble, taps, theme };
